import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import SafetyPledgeDialog from "@/components/SafetyPledgeDialog";
import { useSEO } from "@/hooks/useSEO";
import JsonLd from "@/components/JsonLd";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { IMAGES, AMAZON_URL, LESSONS, BOOKS, SONGS, MUSIC_VIDEO, YOUTUBE_EPISODE } from "@/data/brand";
import { ArrowRight, Check, Star, Tv, Music, ShieldCheck, Award } from "lucide-react";

function BookHero() {
  return (
    <section className="pt-36 pb-24 bg-sq-ink text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-4">The debut book · Available now</p>
          <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.9]">
            Fire Safety with Bobby & Pluggy
          </h1>
          <div className="flex items-center gap-2 mt-4 text-sq-yellow">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-sq-yellow" />)}
            <span className="text-white/60 text-sm ml-2">Loved by parents & teachers</span>
          </div>
          <p className="text-white/70 text-lg mt-6 max-w-lg leading-relaxed">
            The very first Safety Quest adventure. Follow Bobby and Pluggy as they teach young readers
            how to prevent fires, escape safely, and become confident Safety Heroes at home.
          </p>
          <a href={AMAZON_URL} target="_blank" rel="noopener noreferrer" data-testid="book-amazon-cta"
            className="inline-flex items-center gap-2 mt-8 px-7 py-4 rounded-full bg-sq-yellow text-sq-ink font-bold text-lg btn-chunky">
            Buy on Amazon <ArrowRight size={20} />
          </a>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative grid place-items-center">
            {/* spotlight */}
            <div className="absolute w-[26rem] h-[26rem] rounded-full bg-sq-red/40 blur-3xl" />
            <motion.img
              src={IMAGES.bookCover} alt="Fire Safety with Bobby and Pluggy book cover"
              className="relative w-72 sm:w-80 rounded-xl shadow-2xl"
              initial={{ rotate: -6 }} whileHover={{ rotate: 0, scale: 1.03 }}
              transition={{ type: "spring", stiffness: 200 }}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function KidsCorner() {
  const [active, setActive] = useState(LESSONS[0].id);
  const lesson = LESSONS.find((l) => l.id === active);

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Kids' Corner</p>
        <h2 className="font-display text-5xl lg:text-7xl tracking-tight">Practice your safety moves</h2>
        <p className="text-sq-ink/70 text-lg mt-4 max-w-xl">Tap a mission to learn the steps every Safety Hero knows by heart.</p>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-8 mt-14">
        {/* Lesson picker */}
        <div className="lg:col-span-4 space-y-4">
          {LESSONS.map((l) => (
            <button
              key={l.id}
              data-testid={`lesson-${l.id}`}
              onClick={() => setActive(l.id)}
              className={`w-full text-left rounded-3xl border-2 border-sq-ink p-6 transition-transform hover:-translate-y-1 ${
                active === l.id ? `bg-${l.color} ${l.color === "sq-yellow" ? "text-sq-ink" : "text-white"} shadow-[0_6px_0_#111827]` : "bg-white"
              }`}
            >
              <span className="font-display text-3xl tracking-wide">{l.title}</span>
            </button>
          ))}
        </div>

        {/* Lesson detail */}
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
              className="rounded-[2rem] bg-white border-2 border-sq-ink p-8 lg:p-10 shadow-[0_8px_0_#111827] h-full"
            >
              <h3 className="font-round text-3xl font-semibold">{lesson.title}</h3>
              <div className="grid sm:grid-cols-3 gap-4 mt-8">
                {lesson.steps.map((s, i) => (
                  <div key={i} className={`rounded-2xl bg-${lesson.color}/10 border border-${lesson.color}/30 p-5`}>
                    <span className={`grid place-items-center w-9 h-9 rounded-full bg-${lesson.color} ${lesson.color === "sq-yellow" ? "text-sq-ink" : "text-white"} font-display text-xl mb-3`}>{i + 1}</span>
                    <p className="font-medium text-sq-ink/80">{s}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-3 mt-8 bg-sq-ink text-white rounded-2xl p-5">
                <Check className="text-sq-yellow shrink-0 mt-0.5" />
                <p className="font-medium">{lesson.tip}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function BooksLibrary() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">The Safety Quest Library</p>
        <h2 className="font-display text-5xl lg:text-7xl tracking-tight">Two adventures, endless lessons</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-8 mt-14">
        {BOOKS.map((b, i) => (
          <Reveal key={b.id} delay={i * 0.1}>
            <div data-testid={`book-${b.id}`} className="h-full rounded-[2rem] bg-white border-2 border-sq-ink p-8 shadow-[0_8px_0_#111827] flex flex-col sm:flex-row gap-7">
              <img src={b.cover} alt={`${b.title} book cover`} className="w-40 self-center sm:self-start rounded-xl shadow-lg shrink-0" />
              <div className="flex flex-col">
                <span className="text-xs font-bold uppercase tracking-widest text-sq-ink/40">{b.series}{b.available ? " · Available Now" : " · Coming Soon"}</span>
                <h3 className="font-round text-2xl font-semibold mt-1 leading-tight">{b.title}</h3>
                <p className="text-sq-ink/70 mt-3 leading-relaxed text-sm flex-1">{b.blurb}</p>
                <a href={b.amazon} target="_blank" rel="noopener noreferrer" data-testid={`buy-${b.id}`}
                  className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-sq-red text-white font-bold btn-chunky self-start">
                  Buy on Amazon <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function SongsSection() {
  return (
    <section id="songs" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-24">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Safety Sing-Along Songs · Now Playing</p>
        <h2 className="font-display text-5xl lg:text-7xl tracking-tight">Turn safety rules into anthems</h2>
        <p className="text-sq-ink/70 text-lg mt-4 max-w-xl">
          The official sing-along songs from the upcoming cartoon — the fun, catchy way to make every
          safety lesson stick. Press play and shuffle along with Bobby & Pluggy!
        </p>
      </Reveal>

      {/* Featured music video */}
      <Reveal delay={0.05}>
        <div className="mt-12 rounded-[2rem] overflow-hidden border-4 border-sq-ink shadow-[0_10px_0_#111827] bg-black aspect-video">
          <video
            src={MUSIC_VIDEO}
            controls
            playsInline
            preload="metadata"
            poster={IMAGES.heroScene}
            data-testid="music-video"
            className="w-full h-full object-contain bg-black"
          />
        </div>
        <p className="text-sq-ink/50 text-sm mt-3">
          Video not playing? <a href={MUSIC_VIDEO} target="_blank" rel="noopener noreferrer" className="underline hover:text-sq-red">Open it directly →</a>
        </p>
      </Reveal>

      <div className="grid sm:grid-cols-2 gap-8 mt-14">
        {SONGS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08}>
            <div data-testid={`song-${s.id}`} className="rounded-[1.8rem] bg-white border-2 border-sq-ink p-4 shadow-[0_6px_0_#111827]">
              <div className="rounded-2xl overflow-hidden bg-black aspect-video">
                <video
                  src={s.url}
                  controls
                  preload="metadata"
                  playsInline
                  data-testid={`song-video-${s.id}`}
                  className="w-full h-full object-contain bg-black"
                />
              </div>
              <div className="flex items-center gap-3 px-2 pt-4 pb-1">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-sq-red text-white shrink-0"><Music size={18} /></span>
                <div>
                  <h3 className="font-round text-xl font-semibold leading-tight">{s.title}</h3>
                  <p className="text-sq-ink/60 text-sm">{s.desc}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function BooksMedia() {
  useSEO({ title: "Books & Media — Fire Safety with Bobby and Pluggy", description: "Buy the debut children's book Fire Safety with Bobby and Pluggy, explore interactive safety lessons, and take the Safety Hero Pledge." });

  return (
    <>
      <JsonLd id="books" data={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: BOOKS.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Book",
            name: b.title,
            author: { "@type": "Person", name: "Bobby Martin" },
            image: b.cover,
            url: b.amazon,
            offers: { "@type": "Offer", availability: "https://schema.org/InStock", url: b.amazon },
          },
        })),
      }} />
      <BookHero />
      <BooksLibrary />

      {/* Episode 1 */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Now Streaming · Episode 1</p>
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight leading-[0.9]">Watch the first episode</h2>
          <p className="font-body text-lg text-sq-ink/70 mt-4 max-w-xl">
            The debut episode of The Safety Quest animated series is here! Click play to join Bobby &amp;
            Pluggy on their very first on-screen adventure.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10">
            <YouTubeEmbed id={YOUTUBE_EPISODE.id} title={YOUTUBE_EPISODE.title} />
          </div>
        </Reveal>
      </section>

      <KidsCorner />
      <SongsSection />

      {/* Safety Hero Pledge */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-[2.5rem] border-4 border-sq-ink bg-sq-yellow p-10 lg:p-16 text-center">
          <Award className="mx-auto text-sq-red" size={48} />
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight mt-4">The Safety Hero Pledge</h2>
          <p className="font-round text-lg text-sq-ink/80 mt-4 max-w-xl mx-auto">
            Make the promise, personalize your certificate, and print it out to show you're an official Safety Hero!
          </p>
          <div className="mt-8 flex justify-center">
            <SafetyPledgeDialog trigger={
              <button data-testid="pledge-section-btn" className="px-8 py-4 rounded-full bg-sq-red text-white font-bold text-lg btn-chunky inline-flex items-center gap-2">
                <ShieldCheck size={20} /> Sign the Pledge
              </button>
            } />
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="bg-sq-ink text-white py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-3">Coming Soon</p>
            <h2 className="font-display text-5xl lg:text-7xl tracking-tight">The adventure keeps growing</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {[
              { icon: Tv, t: "More Episodes", d: "Episode 1 is here — new episodes of the animated series are on the way." },
              { icon: Music, t: "More Music Videos", d: "New sing-along songs and the full soundtrack drop alongside the animated series." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.1}>
                <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-8 relative overflow-hidden">
                  <span className="absolute top-6 right-6 text-xs font-bold uppercase tracking-widest bg-sq-red px-3 py-1 rounded-full">Soon</span>
                  <c.icon className="text-sq-yellow mb-5" size={34} />
                  <h3 className="font-round text-3xl font-semibold">{c.t}</h3>
                  <p className="text-white/60 mt-3 leading-relaxed max-w-md">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
